import { useTranslation } from "react-i18next";

export function useTimeAgo() {
  const { t } = useTranslation();

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;

    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffYear > 0) return t("time.years_ago", { count: diffYear });
    if (diffMonth > 0) return t("time.months_ago", { count: diffMonth });
    if (diffWeek > 0) return t("time.weeks_ago", { count: diffWeek });
    if (diffDay > 0) return t("time.days_ago", { count: diffDay });
    if (diffHour > 0) return t("time.hours_ago", { count: diffHour });
    if (diffMin > 0) return t("time.minutes_ago", { count: diffMin });

    return t("time.just_now");
  };

  return { getTimeAgo };
}

/**
 * The group feed's own stamp: relative while a post is fresh, then a plain date.
 *
 * Deliberately not {@link useTimeAgo} — a post from March reading "5 сарын өмнө" tells you
 * less than "3/14/25" does, and the feed is a chronology. Takes `t` rather than calling the
 * hook so it can be used inside a map without a component per row.
 */
export const formatPostTime = (dateString, t) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay >= 1) {
    return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
  }
  if (diffHour > 0) return t("time.hours_ago", { count: diffHour });
  if (diffMin > 0) return t("time.minutes_ago", { count: diffMin });
  return t("time.just_now");
};

/**
 * The admin CV bank's presence stamp: "1ц 12м", short enough to sit beside a name.
 *
 * Deliberately not {@link useTimeAgo}, which is a hook and produces the long translated
 * form ("2 өдрийн өмнө"). This one is called once per row on a list of fifty, needs to fit
 * in a pill next to a name that is already truncating, and only ever appears on a screen
 * that is hardcoded Mongolian — so a plain function with Mongolian abbreviations, not a
 * hook and not i18n. The long readable form is still what goes in the tooltip.
 *
 * Takes a Date, not a string: the caller has already had to parse and validate the
 * server's "yyyy-MM-dd HH:mm:ss" (see PresenceDot), and parsing it twice invites the two
 * places to disagree about what an unparseable value means.
 */
export const formatPresenceShort = (date) => {
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);

  if (diffMin < 1) return "саяхан";
  if (diffMin < 60) return `${diffMin}м`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    const minutes = diffMin % 60;
    // The minutes are worth showing at this range — "1ц" and "1ц 59м" are an hour apart —
    // but "3ц 0м" is noise, so a whole hour drops them.
    return minutes > 0 ? `${diffHour}ц ${minutes}м` : `${diffHour}ц`;
  }

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}ө`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} дол`;
  if (diffDay < 365) return `${Math.floor(diffDay / 30)} сар`;
  return `${Math.floor(diffDay / 365)} жил`;
};
